import { SUPPORTED_LOCALES } from 'locales/constants';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for managing language switching and translation utilities.
 * Provides methods to switch between supported languages and access i18n functionality.
 *
 * @returns {{
 *   t: Function,
 *   i18n: Object,
 *   currentLanguage: string,
 *   switchLanguage: (targetLang: string) => void,
 *   isRTL: boolean
 * }} Language switcher utilities
 * @property {Function} t - Translation function from react-i18next
 * @property {Object} i18n - i18next instance with full API access
 * @property {string} currentLanguage - Currently active language code (e.g., "en", "es")
 * @property {Function} switchLanguage - Function to switch to a different language
 * @property {boolean} isRTL - Whether the current language uses right-to-left text direction
 */
export const useLanguageSwitcher = () => {
    const { t, i18n } = useTranslation();

    /**
     * Switches the application language by updating the URL locale prefix.
     * Validates the target language and redirects to the appropriate localized URL.
     *
     * @param {string} targetLang - The target language code (e.g., "en", "es")
     * @returns {void}
     *
     * @example
     * const { switchLanguage } = useLanguageSwitcher();
     * switchLanguage('es'); // Redirects from /en/dashboard to /es/dashboard
     */
    const switchLanguage = useCallback(
        (targetLang) => {
            if (!targetLang) return;
            if (!SUPPORTED_LOCALES.includes(targetLang)) {
                console.warn(`Language "${targetLang}" is not supported.`);
                return;
            }
            if (targetLang === i18n.language) return;

            const currentUrl = new URL(window.location.href);
            const pathSegments = currentUrl.pathname.split('/').filter(Boolean);
            const currentLangPrefix = pathSegments[0];

            const isCurrentLangSupported = SUPPORTED_LOCALES.includes(currentLangPrefix);

            let newSegments = [...pathSegments];

            if (isCurrentLangSupported) {
                newSegments[0] = targetLang;
            } else {
                newSegments.unshift(targetLang);
            }

            currentUrl.pathname = `/${newSegments.join('/')}`;

            window.location.assign(currentUrl.toString());
        },
        [i18n.language]
    );

    return {
        t,
        i18n,
        currentLanguage: i18n.language,
        switchLanguage,
        isRTL: i18n.dir() === 'rtl',
    };
};
